/**
 * ChannelGuideOverlay — Floating guide button + dialog for new users.
 *
 * Shows two text links at bottom of inbox page:
 *   1. "เชื่อมต่อช่องทาง" — opens channel connection drawer
 *   2. "คุยกับทีมซัพพอร์ต" — links to Zaviago LINE OA
 *
 * Visibility logic:
 *   - Shows on inbox page (hides when inside a conversation on mobile)
 *   - On desktop: always visible on any /inbox/* route
 *   - When user taps X: collapses to small icon + tooltip for this session
 *   - After 7 days from first dismiss: gone permanently
 *   - When user connects a real channel (chat_channel_connected): gone permanently
 */

import { useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Headset, Plug } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  PRIMARY_CHANNELS,
} from "@/features/dashboard/components/IntentOnboardingPage";
import { captureOnboardingChannelClicked } from "@/lib/analytics/posthog-events";

const DISMISS_KEY = "chat_channel_guide_dismissed_at";
const CONNECTED_KEY = "chat_channel_connected";
const SHOW_FOR_DAYS = 7;
const ZAVIAGO_LINE_OA_URL = "https://line.me/R/ti/p/@zaviago";

export function ChannelGuideOverlay() {
  const location = useLocation();
  const isDesktop = useMediaQuery("(min-width: 768px)");

  // On mobile, hide when user is inside a conversation
  // Chatwoot syncs URL to /inbox/conversations/123 when in a chat
  const isInsideConversation = location.pathname.includes("/conversations/");
  if (!isDesktop && isInsideConversation) return null;

  return <ChannelGuideContent />;
}

/** Inner component — separated so hooks aren't called after early return */
function ChannelGuideContent() {
  const [dismissed, setDismissed] = useState(() => {
    return sessionStorage.getItem("chat_guide_dismissed_session") === "1";
  });

  const visible = (() => {
    if (localStorage.getItem(CONNECTED_KEY) === "1") return false;
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const daysSince = (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSince > SHOW_FOR_DAYS) return false;
    }
    return true;
  })();

  const [dialogOpen, setDialogOpen] = useState(false);

  const dismiss = useCallback(() => {
    if (!localStorage.getItem(DISMISS_KEY)) {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    }
    sessionStorage.setItem("chat_guide_dismissed_session", "1");
    setDismissed(true);
  }, []);

  if (!visible) return null;

  return (
    <>
      {/* Floating guide — bottom-right, above Captain icon */}
      {!dialogOpen && (
        <div className="fixed bottom-36 right-4 md:bottom-6 md:right-20 z-20 flex flex-col items-end gap-2">
          {dismissed ? (
            // Collapsed: two compact links + icon button
            <div className="flex items-end gap-2">
              <div className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-lg border border-border/40 px-3.5 py-2.5 text-xs max-w-[200px] leading-relaxed">
                <button
                  type="button"
                  onClick={() => setDialogOpen(true)}
                  className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                >
                  <Plug size={13} className="shrink-0" />
                  เชื่อมต่อช่องทางแชท
                </button>
                <div className="border-t border-border/30 my-1.5" />
                <a
                  href={ZAVIAGO_LINE_OA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground font-medium"
                >
                  <Headset size={13} className="shrink-0" />
                  คุยกับทีมซัพพอร์ต
                </a>
                {/* Speech bubble arrow pointing right */}
                <div className="absolute -right-1.5 bottom-3 w-3 h-3 bg-white dark:bg-neutral-900 border-r border-b border-border/40 rotate-[-45deg]" />
              </div>
              <button
                type="button"
                onClick={() => setDialogOpen(true)}
                className={cn(
                  "flex items-center justify-center w-12 h-12 rounded-full shrink-0",
                  "bg-blue-500 text-white shadow-lg",
                  "hover:bg-blue-600 active:scale-[0.96]",
                  "transition-all duration-200",
                )}
              >
                <Plug size={20} />
              </button>
            </div>
          ) : (
            // Expanded: two text links + dismiss X
            <div className="flex items-start gap-2">
              <div className="flex flex-col gap-1.5 bg-white dark:bg-neutral-900 rounded-2xl shadow-lg border border-border/40 px-4 py-3">
                <button
                  type="button"
                  onClick={() => setDialogOpen(true)}
                  className={cn(
                    "flex items-center gap-2.5 text-sm font-medium text-blue-600 dark:text-blue-400",
                    "hover:text-blue-700 dark:hover:text-blue-300 active:scale-[0.97]",
                    "transition-all duration-150",
                  )}
                >
                  <Plug size={16} className="shrink-0" />
                  <span>เชื่อมต่อช่องทางแชท</span>
                </button>
                <div className="border-t border-border/30" />
                <a
                  href={ZAVIAGO_LINE_OA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    "flex items-center gap-2.5 text-sm font-medium text-muted-foreground",
                    "hover:text-foreground active:scale-[0.97]",
                    "transition-all duration-150",
                  )}
                >
                  <Headset size={16} className="shrink-0" />
                  <span>คุยกับทีมซัพพอร์ต</span>
                </a>
              </div>
              <button
                type="button"
                onClick={dismiss}
                className="mt-1 p-1.5 rounded-full bg-white/90 dark:bg-neutral-800/90 shadow-md text-muted-foreground hover:text-foreground transition-colors"
                aria-label="ปิด"
              >
                <X size={14} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Channel connection dialog (bottom sheet on mobile, centered on desktop) */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="md:max-w-md">
          <DialogHeader className="mb-2">
            <DialogTitle className="text-base font-semibold text-center">
              เชื่อมต่อช่องทางแชท
            </DialogTitle>
            <p className="text-sm text-muted-foreground text-center">
              เลือกช่องทางที่คุณใช้รับแชทจากลูกค้า
            </p>
          </DialogHeader>

          <div className="grid grid-cols-3 gap-3 max-w-sm mx-auto px-4 md:px-0">
            {PRIMARY_CHANNELS.map((channel) => (
              <Link
                key={channel.id}
                to={channel.connectHref}
                onClick={() => {
                  captureOnboardingChannelClicked(channel.id);
                  dismiss();
                  setDialogOpen(false);
                }}
                className={cn(
                  "flex flex-col items-center gap-3 p-5 pb-4 rounded-2xl",
                  "bg-card border border-border shadow-sm",
                  "hover:shadow-md hover:scale-[1.02] active:scale-[0.97]",
                  "transition-all duration-200",
                )}
              >
                <div
                  className={cn(
                    "w-14 h-14 rounded-2xl flex items-center justify-center",
                    channel.tint,
                  )}
                >
                  {channel.icon}
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-foreground">
                    {channel.name}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    เชื่อมต่อ
                  </p>
                </div>
              </Link>
            ))}
          </div>

          <p className="text-center text-xs text-muted-foreground mt-4 pb-2">
            <button
              type="button"
              onClick={() => {
                dismiss();
                setDialogOpen(false);
              }}
              className="hover:text-foreground transition-colors underline"
            >
              ข้ามไปก่อน — เชื่อมต่อทีหลัง
            </button>
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}
